import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ArrowLeft } from "lucide-react";
import { useContent } from "@/lib/api";
import { Loading, PageHead, Section } from "@/components/site/Bits";

export const Hl = ({ text, q }) => {
  if (!q) return text;
  const i = text.toLowerCase().indexOf(q.toLowerCase());
  if (i < 0) return text;
  return <>{text.slice(0, i)}<mark>{text.slice(i, i + q.length)}</mark>{text.slice(i + q.length)}</>;
};

function Results({ rules, q }) {
  const hits = useMemo(() => {
    const out = [];
    rules.forEach((r) => r.sections.forEach((s) => s.items.forEach((it) => {
      if (it.toLowerCase().includes(q.toLowerCase()) || s.title.toLowerCase().includes(q.toLowerCase())) out.push({ r, s, it });
    })));
    return out.slice(0, 60);
  }, [rules, q]);
  if (!hits.length) return <p className="glass rounded-2xl p-8 text-center text-sm text-slate-400" data-testid="rules-search-empty">ما لقينا شي يطابق "{q}"</p>;
  return (
    <div className="space-y-3" data-testid="rules-search-results">
      <p className="text-xs text-slate-500">{hits.length} نتيجة</p>
      {hits.map(({ r, s, it }, i) => (
        <Link key={i} to={`/rules/${r.slug}?q=${encodeURIComponent(q)}`} className="card-hover glass block rounded-xl p-5" data-testid="rules-search-hit">
          <div className="text-xs text-sky-300">{r.name} · <Hl text={s.title} q={q} /></div>
          <p className="mt-2 text-sm leading-7 text-slate-200"><Hl text={it} q={q} /></p>
        </Link>
      ))}
    </div>
  );
}

export default function Rules() {
  const { data = [], isLoading } = useContent("rules");
  const [q, setQ] = useState("");
  return (
    <>
      <PageHead kicker="CITY RULES" title="قوانين المدينة" desc="اختر القسم اللي تبي تقرأ قوانينه. دخولك للمدينة يعني موافقتك على جميع القوانين.">
        <div className="relative mt-8 max-w-xl">
          <Search size={18} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="ابحث في كل القوانين... مثال: RDM، سرقة، باند" className="field !rounded-full !py-3.5 !pr-11" data-testid="rules-search-input" />
        </div>
      </PageHead>
      {isLoading ? <Loading /> : (
        <Section>
          {q.trim().length > 1 ? <Results rules={data} q={q.trim()} /> : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {data.map((r, i) => (
                <Link key={r.slug} to={`/rules/${r.slug}`} className="card-hover glass group rounded-2xl p-6" data-testid={`rules-cat-${r.slug}`}>
                  <div className="flex items-center justify-between">
                    <span className="kicker">{r.tag}</span>
                    <span className="font-mono2 text-xs text-slate-600">{String(i + 1).padStart(2, "0")}</span>
                  </div>
                  <h3 className="font-disp mt-4 text-xl font-bold">{r.name}</h3>
                  <p className="mt-2 line-clamp-2 text-sm leading-7 text-slate-400">{r.summary}</p>
                  <span className="mt-5 inline-flex items-center gap-2 text-sm text-sky-300 transition-transform group-hover:-translate-x-1">اقرأ القوانين <ArrowLeft size={15} /></span>
                </Link>
              ))}
            </div>
          )}
        </Section>
      )}
    </>
  );
}
